import { useEffect, useMemo, useState } from 'react'
import type { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { Download, FileArchive } from 'lucide-react'
import {
  Button,
  Checkbox,
  Chip,
  DataTable,
  Drawer,
  ExportDrawer,
  SearchInput,
  toast,
} from '@/components/ui'
import {
  downloadBulkPricelistPdfZip,
  useBulkPricelistItems,
  useCustomerPricelists,
} from '../api/queries'
import type { PricelistSummary } from '../api/types'
import { BULK_COLUMNS, resolveCustomerName, type BulkPricelistItem } from '../utils/csv'

/** Selecting more than this many customers at once is disallowed — keeps
 *  the bulk PDF export (server-side, synchronous, one .zip per request)
 *  fast. Matches the backend's `_BULK_PDF_EXPORT_CAP`. */
const BULK_EXPORT_CAP = 25

export interface BulkExportDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Dedicated bulk-export picker for Customer Pricelists — a proper
 * sortable/searchable/select-all table, deliberately separate from the
 * `MasterDetail` browse rail (single-customer browse/edit stays a plain
 * search-only rail, matching Vendor Pricelists exactly). Chris's feedback
 * on the first attempt (checkboxes crammed into the 320px rail): "can't
 * sort or filter customers and boxes I would have to endlessly scroll to
 * check for the customer pricelists I want to export."
 */
export function BulkExportDrawer({ open, onOpenChange }: BulkExportDrawerProps) {
  const [search, setSearch] = useState('')
  const [selection, setSelection] = useState<RowSelectionState>({})
  const [pdfPending, setPdfPending] = useState(false)
  const [csvIds, setCsvIds] = useState<number[] | null>(null)
  const [csvOpen, setCsvOpen] = useState(false)

  // Empty query = full list. Shares the React Query cache with the main
  // page's own (also-unfiltered) load, so opening this drawer is usually
  // a cache hit rather than a fresh request.
  const { data, isLoading } = useCustomerPricelists('')
  const pricelists = useMemo<PricelistSummary[]>(() => data?.pricelists ?? [], [data])

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = q
      ? pricelists.filter((p) => resolveCustomerName(p).toLowerCase().includes(q))
      : pricelists
    return [...filtered].sort((a, b) => resolveCustomerName(a).localeCompare(resolveCustomerName(b)))
  }, [pricelists, search])

  const selectedIds = useMemo(
    () =>
      Object.entries(selection)
        .filter(([, checked]) => checked)
        .map(([id]) => Number(id))
        .filter((n) => Number.isFinite(n)),
    [selection],
  )
  const selectedCount = selectedIds.length

  // Central cap enforcement — rejects any update that would exceed the
  // cap (so a stray "select all" over cap can't sneak through). Every
  // path that mutates selection (row toggle, select-all, both routed
  // through DataTable's controlled `onSelectionChange`) funnels through
  // this one place.
  function applySelection(next: RowSelectionState) {
    const count = Object.values(next).filter(Boolean).length
    if (count > BULK_EXPORT_CAP) return
    setSelection(next)
  }

  // Truly static (empty deps) — every closure below reads state via the
  // `row`/`table` argument TanStack passes at render time (`row.getIsSelected()`,
  // `table.getState().rowSelection`, etc.), never via an outer closure
  // over this component's own `selection`/`rows` state. That's the same
  // shape `ProductCustomerPricingPage`'s `__select` column uses. An
  // earlier version of this column closed over `selection`/`rows`
  // directly, which meant `columns` had to change identity on every
  // selection change to stay non-stale — and that identity churn made
  // DataTable remount every row's checkbox on each click (verified: the
  // previously-clicked-away-from checkbox's DOM node went `isConnected
  // === false` right after a sibling checkbox was toggled), silently
  // dropping the very next click. Reading state fresh through `row`/
  // `table` avoids needing `columns` to ever change at all.
  const columns = useMemo<ColumnDef<PricelistSummary, unknown>[]>(
    () => [
      {
        id: '__select',
        header: ({ table }) => (
          <Checkbox
            aria-label="Select all rows"
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
            onChange={() => {
              // Custom rather than `table.getToggleAllRowsSelectedHandler()`
              // — the default would try to select every currently-visible
              // (filtered) row, which `applySelection` would then reject
              // outright once it exceeds the cap. Selecting up to the cap
              // (with a toast explaining the truncation) is more useful
              // than a click that visibly does nothing.
              const visible = table.getRowModel().rows
              if (table.getIsAllRowsSelected()) {
                table.resetRowSelection()
                return
              }
              if (visible.length > BULK_EXPORT_CAP) {
                toast.warning(
                  `Only the first ${BULK_EXPORT_CAP} of ${visible.length} matching customers were selected — narrow your search to export the rest.`,
                )
              }
              const next: RowSelectionState = {}
              visible.slice(0, BULK_EXPORT_CAP).forEach((r) => {
                next[r.id] = true
              })
              table.setRowSelection(next)
            }}
          />
        ),
        cell: ({ row, table }) => {
          const pricelist = row.original
          const isSelected = row.getIsSelected()
          const selectedCount = Object.keys(table.getState().rowSelection).length
          const atCap = !isSelected && selectedCount >= BULK_EXPORT_CAP
          return (
            <Checkbox
              aria-label={
                isSelected
                  ? `Deselect ${resolveCustomerName(pricelist)}`
                  : `Select ${resolveCustomerName(pricelist)}`
              }
              checked={isSelected}
              disabled={atCap}
              onChange={row.getToggleSelectedHandler()}
            />
          )
        },
        enableSorting: false,
      },
      {
        id: 'customer',
        header: 'Customer',
        accessorFn: (r) => resolveCustomerName(r),
        cell: (ctx) => ctx.getValue() as string,
      },
      {
        id: 'item_count',
        header: 'Items',
        accessorFn: (r) => r.item_count,
        cell: (ctx) => (ctx.getValue() as number).toLocaleString(),
      },
      {
        id: 'active',
        header: 'Status',
        accessorFn: (r) => r.active,
        cell: (ctx) =>
          ctx.getValue() ? (
            <Chip size="xs" variant="neutral">
              Active
            </Chip>
          ) : (
            <Chip size="xs" variant="warning">
              Inactive
            </Chip>
          ),
      },
    ],
    [],
  )

  const bulkCsvItems = useBulkPricelistItems(csvIds)

  async function handleExportPdf() {
    if (selectedCount === 0) return
    setPdfPending(true)
    try {
      await downloadBulkPricelistPdfZip(selectedIds)
      toast.success(`Exported ${selectedCount} pricelist PDF${selectedCount === 1 ? '' : 's'}`)
    } catch (err) {
      toast.danger(err instanceof Error ? err.message : 'Bulk PDF export failed')
    } finally {
      setPdfPending(false)
    }
  }

  function handleExportCsvClick() {
    if (selectedCount === 0) return
    onOpenChange(false)
    setCsvIds(selectedIds)
  }

  // Opens the CSV export drawer once the fanned-out per-pricelist item
  // fetches resolve. Legitimate setState-in-effect — reacting to a query
  // settling (external async state), not to local render state.
  useEffect(() => {
    if (!csvIds) return
    if (bulkCsvItems.isSuccess) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCsvOpen(true)
    } else if (bulkCsvItems.isError) {
      toast.danger("Couldn't load items for the bulk CSV export.")
      setCsvIds(null)
    }
  }, [csvIds, bulkCsvItems.isSuccess, bulkCsvItems.isError])

  const bulkCsvRows = useMemo<BulkPricelistItem[]>(() => {
    if (!csvIds || !bulkCsvItems.data) return []
    return csvIds.flatMap((id, idx) => {
      const resp = bulkCsvItems.data[idx]
      const pricelist = pricelists.find((p) => p.id === id)
      const customerName = pricelist ? resolveCustomerName(pricelist) : `Pricelist ${id}`
      return (resp?.items ?? [])
        .filter((it) => it.compute_price === 'fixed')
        .map((it) => ({ ...it, __customerName: customerName }))
    })
  }, [csvIds, bulkCsvItems.data, pricelists])

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        size="xl"
        title="Bulk export pricelists"
        meta={`${pricelists.length.toLocaleString()} customers`}
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <span className="text-sm text-secondary">
              {selectedCount} selected{selectedCount >= BULK_EXPORT_CAP ? ` (max ${BULK_EXPORT_CAP})` : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={() => void handleExportPdf()}
                disabled={selectedCount === 0 || pdfPending}
              >
                <FileArchive size={14} />
                {pdfPending ? 'Exporting…' : `Export PDFs (${selectedCount})`}
              </Button>
              <Button
                variant="primary"
                onClick={handleExportCsvClick}
                disabled={selectedCount === 0}
              >
                <Download size={14} /> Export CSV…
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex h-full flex-col gap-3">
          <SearchInput
            placeholder={`Search ${pricelists.length} customers`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            containerClassName="max-w-none"
          />
          <DataTable
            data={rows}
            columns={columns}
            loading={isLoading}
            selection={selection}
            onSelectionChange={applySelection}
            getRowId={(r) => String(r.id)}
            maxHeight="100%"
            stickyFirstColumn={false}
            emptyState={<p className="p-4 text-sm text-tertiary">No customers match "{search}".</p>}
          />
        </div>
      </Drawer>

      <ExportDrawer
        open={csvOpen}
        onOpenChange={(next) => {
          setCsvOpen(next)
          if (!next) setCsvIds(null)
        }}
        rows={bulkCsvRows}
        columns={BULK_COLUMNS}
        moduleId="customer-pricelists-bulk"
        filenameBase="pricelists-bulk"
        title="Export selected pricelists to CSV"
      />
    </>
  )
}
